import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { getFirebaseAnalytics, isAnalyticsAvailable } from './firebase';
import { createSafeLogData, sanitizeLogMessage } from '../utils/pii-masker';

// Interface para eventos customizados
interface CustomEvent {
  event_name: string;
  parameters?: Record<string, any>;
}

// Classe para gerenciar Firebase Analytics
class FirebaseAnalyticsService {
  private analytics = getFirebaseAnalytics();

  // Verificar se analytics está disponível
  private isAvailable(): boolean {
    return isAnalyticsAvailable() && this.analytics !== null;
  }

  // Log de evento personalizado (sem PII)
  logEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isAvailable()) {
      console.warn('Firebase Analytics não está disponível');
      return;
    }

    try {
      // Remover PII dos parâmetros
      const safeParameters = createSafeLogData(parameters);
      
      // Adicionar identificação da plataforma (sem dados pessoais)
      const eventParameters = {
        ...safeParameters,
        platform: 'web',
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV || 'development'
      };

      logEvent(this.analytics!, eventName, eventParameters);
      console.log(`[Firebase Analytics] Evento: ${eventName}`, eventParameters);
    } catch (error) {
      console.error('Erro ao enviar evento para Firebase Analytics:', error);
    }
  }

  // Log de evento de página
  logPageView(pageName: string, pageTitle?: string): void {
    this.logEvent('page_view', {
      page_name: pageName,
      page_title: pageTitle || document.title,
      page_location: window.location.href
    });
  }

  // Log de evento de login
  logLogin(method: string = 'email'): void {
    this.logEvent('login', {
      method: method
    });
  }

  // Log de evento de logout
  logLogout(): void {
    this.logEvent('logout');
  }

  // Log de evento de cadastro
  logSignUp(method: string = 'email'): void {
    this.logEvent('sign_up', {
      method: method
    });
  }

  // Log de evento de busca (sem termos de busca pessoais)
  logSearch(searchTerm: string, category?: string): void {
    // Não logar o termo de busca para preservar privacidade
    this.logEvent('search', {
      search_category: category || 'general',
      has_search_term: !!searchTerm
    });
  }

  // Log de evento de visualização de vaga
  logJobView(jobId: string, jobTitle: string, companyName: string): void {
    this.logEvent('view_job', {
      job_id: jobId,
      job_title: jobTitle,
      company_name: companyName
    });
  }

  // Log de evento de candidatura
  logJobApplication(jobId: string, jobTitle: string, companyName: string): void {
    this.logEvent('job_application', {
      job_id: jobId,
      job_title: jobTitle,
      company_name: companyName
    });
  }

  // Log de evento de salvamento de vaga
  logJobSave(jobId: string, jobTitle: string, companyName: string): void {
    this.logEvent('job_save', {
      job_id: jobId,
      job_title: jobTitle,
      company_name: companyName
    });
  }

  // Log de evento de criação de vaga
  logJobCreate(jobTitle: string, jobType: string): void {
    this.logEvent('job_create', {
      job_title: jobTitle,
      job_type: jobType
    });
  }

  // Log de evento de erro
  logError(errorMessage: string, errorCode?: string, fatal: boolean = false): void {
    this.logEvent('error', {
      error_message: errorMessage,
      error_code: errorCode,
      fatal: fatal
    });
  }

  // Definir ID do usuário
  setUserId(userId: string): void {
    if (!this.isAvailable()) {
      console.warn('Firebase Analytics não está disponível');
      return;
    }

    try {
      setUserId(this.analytics!, userId);
      console.log(`[Firebase Analytics] User ID definido: ${userId}`);
    } catch (error) {
      console.error('Erro ao definir User ID no Firebase Analytics:', error);
    }
  }

  // Definir propriedades do usuário (sem PII)
  setUserProperties(properties: Record<string, any>): void {
    if (!this.isAvailable()) {
      console.warn('Firebase Analytics não está disponível');
      return;
    }

    try {
      // Remover PII das propriedades
      const safeProperties = createSafeLogData(properties);
      
      // Adicionar propriedades de identificação da plataforma (sem dados pessoais)
      const userProperties = {
        ...safeProperties,
        platform: 'web',
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV || 'development',
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language
        // Removido user_agent para preservar privacidade
      };

      setUserProperties(this.analytics!, userProperties);
      console.log('[Firebase Analytics] Propriedades do usuário definidas:', userProperties);
    } catch (error) {
      console.error('Erro ao definir propriedades do usuário no Firebase Analytics:', error);
    }
  }

  // Limpar dados do usuário (logout)
  clearUserData(): void {
    this.setUserId('');
    this.setUserProperties({});
  }
}

// Instância singleton
export const firebaseAnalytics = new FirebaseAnalyticsService();

// Exportar tipos
export type { CustomEvent };
